import { useEffect, useRef, useState } from 'react';
import { View, ActivityIndicator, Pressable, Text, Platform, TextInput } from 'react-native';
import { useRouter } from 'expo-router';
import {
  consumePendingAuthRedirect,
  normalizeAuthRedirect,
} from '@/lib/auth-redirect';
import {
  consumePendingAuthAttempt,
  getPendingAuthAttempt,
  wasAuthUserCreatedDuringAttempt,
} from '@/lib/auth-attempt';
import {
  trackAuthAttemptFailed,
  trackAuthAttemptSucceeded,
  trackAuthUserCreated,
} from '@/lib/product-events';
import { supabase } from '@/lib/supabase';
import { PasswordRequirementList } from '@/components';
import { getPasswordPolicyError } from '@/lib/password-policy';

const SESSION_POLL_INTERVAL_MS = 100;
const SESSION_WAIT_TIMEOUT_MS = 3_000;
const POST_AUTH_SETTLE_DELAY_MS = 300;
const PASSWORD_RESET_PENDING_STORAGE_KEY = 'perdiem.password-reset-pending';
const PASSWORD_RESET_PENDING_MAX_AGE_MS = 30 * 60 * 1000;

async function waitForPersistedSession(): Promise<boolean> {
  const deadline = Date.now() + SESSION_WAIT_TIMEOUT_MS;

  while (Date.now() < deadline) {
    const { data } = await supabase.auth.getSession();
    if (data.session?.access_token) {
      return true;
    }

    await new Promise((resolve) => {
      setTimeout(resolve, SESSION_POLL_INTERVAL_MS);
    });
  }

  return false;
}

function decodeAuthParam(value: string | null): string | null {
  if (!value) return null;

  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
}

function buildCallbackErrorMessage(params: URLSearchParams): string | null {
  const errorCode = decodeAuthParam(params.get('error_code'));
  const errorDescription = decodeAuthParam(params.get('error_description'));
  const error = decodeAuthParam(params.get('error'));

  if (errorCode === 'otp_expired') {
    return 'This confirmation link has expired. Request a new confirmation email and try again.';
  }

  if (errorDescription) {
    return errorDescription;
  }

  if (error) {
    return error;
  }

  return null;
}

function getCallbackErrorMessage(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  const url = new URL(window.location.href);
  const searchMessage = buildCallbackErrorMessage(url.searchParams);
  if (searchMessage) return searchMessage;

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) return null;

  return buildCallbackErrorMessage(new URLSearchParams(hash));
}

function getCallbackAuthRedirect(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return null;
  }

  const url = new URL(window.location.href);
  const searchRedirect = normalizeAuthRedirect(url.searchParams.get('redirect'));
  if (searchRedirect) return searchRedirect;

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) return null;

  return normalizeAuthRedirect(new URLSearchParams(hash).get('redirect'));
}

function clearCallbackHash() {
  if (Platform.OS !== 'web' || typeof window === 'undefined' || !window.location.hash) {
    return;
  }

  window.history.replaceState({}, document.title, `${window.location.pathname}${window.location.search}`);
}

function isRecoveryCallback(): boolean {
  if (Platform.OS !== 'web' || typeof window === 'undefined') {
    return false;
  }

  const url = new URL(window.location.href);
  if (
    url.searchParams.get('type') === 'recovery' ||
    url.searchParams.get('authFlow') === 'password-reset'
  ) {
    return true;
  }

  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (!hash) return false;

  const hashParams = new URLSearchParams(hash);
  return (
    hashParams.get('type') === 'recovery' ||
    hashParams.get('authFlow') === 'password-reset'
  );
}

function hasPendingPasswordResetMarker(): boolean {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return false;
  }

  try {
    const rawValue = localStorage.getItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
    if (!rawValue) return false;

    const parsed = JSON.parse(rawValue) as { createdAt?: unknown };
    if (typeof parsed.createdAt !== 'string') {
      localStorage.removeItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
      return false;
    }

    const createdAt = Date.parse(parsed.createdAt);
    if (!Number.isFinite(createdAt)) {
      localStorage.removeItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
      return false;
    }

    const isFresh = Date.now() - createdAt < PASSWORD_RESET_PENDING_MAX_AGE_MS;
    if (!isFresh) {
      localStorage.removeItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
    }

    return isFresh;
  } catch {
    return false;
  }
}

function clearPendingPasswordResetMarker() {
  if (Platform.OS !== 'web' || typeof localStorage === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(PASSWORD_RESET_PENDING_STORAGE_KEY);
  } catch {
    // Ignore storage cleanup failures.
  }
}

/**
 * OAuth redirect target — Supabase completes the sign-in here.
 *
 * On web (PKCE flow): Supabase automatically exchanges the `?code=` param for
 * a session when `detectSessionInUrl: true` is set. We listen for SIGNED_IN to
 * navigate after the exchange completes rather than calling getSession() eagerly
 * (which would race against the async code exchange).
 *
 * On native: the login screen finishes the session after the browser returns,
 * and this route remains a safe landing target if the app deep-links here.
 */
export default function AuthCallbackScreen() {
  const router = useRouter();
  const didRedirectRef = useRef(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const handleUpdatePassword = async () => {
    setResetError(null);

    if (password !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }

    const passwordPolicyError = getPasswordPolicyError(password);
    if (passwordPolicyError) {
      setResetError(passwordPolicyError);
      return;
    }

    setIsUpdatingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        setResetError(error.message);
        return;
      }

      clearPendingPasswordResetMarker();
      router.replace('/(app)' as any);
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  useEffect(() => {
    const callbackError = getCallbackErrorMessage();
    if (callbackError) {
      clearCallbackHash();
      void (async () => {
        const pendingAttempt = await consumePendingAuthAttempt();
        if (!pendingAttempt) return;

        trackAuthAttemptFailed({
          route: pendingAttempt.route,
          intent: pendingAttempt.intent,
          provider: pendingAttempt.provider,
          flow: pendingAttempt.flow,
          failureStage: 'callback',
          errorBucket: callbackError.slice(0, 80),
          errorMessage: callbackError,
        });
      })();
      setErrorMessage(callbackError);
      return;
    }

    const recoveryCallback = isRecoveryCallback() || hasPendingPasswordResetMarker();
    if (recoveryCallback) {
      setIsPasswordRecovery(true);
    }

    const redirectAfterAuth = async () => {
      if (didRedirectRef.current) return;
      didRedirectRef.current = true;

      const sessionReady = await waitForPersistedSession();
      if (!sessionReady) {
        const pendingAttempt = await consumePendingAuthAttempt();
        if (pendingAttempt) {
          trackAuthAttemptFailed({
            route: pendingAttempt.route,
            intent: pendingAttempt.intent,
            provider: pendingAttempt.provider,
            flow: pendingAttempt.flow,
            failureStage: 'session_exchange',
            errorBucket: 'session_timeout',
            errorMessage: 'Sign-in did not complete. Please try again.',
          });
        }
        setErrorMessage('Sign-in did not complete. Please try again.');
        return;
      }

      await new Promise((resolve) => {
        setTimeout(resolve, POST_AUTH_SETTLE_DELAY_MS);
      });
      const { data: sessionData } = await supabase.auth.getSession();

      const pendingAttempt = await consumePendingAuthAttempt();
      if (pendingAttempt) {
        const createdNewUser = wasAuthUserCreatedDuringAttempt({
          attemptStartedAt: pendingAttempt.startedAt,
          userCreatedAt: sessionData.session?.user.created_at ?? null,
        });
        if (createdNewUser) {
          trackAuthUserCreated({
            route: pendingAttempt.route,
            attemptedIntent: pendingAttempt.intent,
            provider: pendingAttempt.provider,
            flow: pendingAttempt.flow,
            supabaseUserId: sessionData.session?.user.id ?? null,
          });
        }
        trackAuthAttemptSucceeded({
          route: pendingAttempt.route,
          intent: pendingAttempt.intent,
          provider: pendingAttempt.provider,
          flow: pendingAttempt.flow,
          metadata: {
            attemptedIntent: pendingAttempt.intent,
            createdNewUser,
          },
        });
      }

      const callbackRedirect = getCallbackAuthRedirect();
      const pendingRedirect = await consumePendingAuthRedirect();
      // Return to the authenticated app shell once the session is readable.
      // The app shell itself handles redirecting incomplete households back to
      // onboarding without mounting dashboard queries too early.
      router.replace((callbackRedirect ?? pendingRedirect ?? '/(app)') as any);
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
        return;
      }

      if (session) {
        if (recoveryCallback) {
          setIsPasswordRecovery(true);
          return;
        }
        void redirectAfterAuth();
      } else if (event === 'SIGNED_OUT') {
        void consumePendingAuthAttempt();
        router.replace('/(auth)/login');
      }
    });

    // Also check in case the session was already established before this mounts
    // (e.g. on native where the redirect arrives synchronously).
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        if (recoveryCallback) {
          setIsPasswordRecovery(true);
          return;
        }
        void redirectAfterAuth();
      } else {
        void getPendingAuthAttempt();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  if (isPasswordRecovery) {
    return (
      <View className="flex-1 items-center justify-center bg-gray-950 px-6">
        <View className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-6">
          <Text className="mb-3 text-2xl font-semibold text-white">Reset Password</Text>
          <Text className="mb-5 text-sm leading-6 text-gray-300">
            Choose a new password for your Per Diem account.
          </Text>
          <TextInput
            className="mb-2 w-full rounded-xl bg-gray-800 px-4 py-4 text-base text-white"
            placeholder="New password"
            placeholderTextColor="#6b7280"
            value={password}
            onChangeText={(value) => {
              setPassword(value);
              setResetError(null);
            }}
            secureTextEntry
            autoComplete="new-password"
          />
          <PasswordRequirementList password={password} className="mb-3" />
          <TextInput
            className="mb-4 w-full rounded-xl bg-gray-800 px-4 py-4 text-base text-white"
            placeholder="Confirm new password"
            placeholderTextColor="#6b7280"
            value={confirmPassword}
            onChangeText={(value) => {
              setConfirmPassword(value);
              setResetError(null);
            }}
            secureTextEntry
            autoComplete="new-password"
            returnKeyType="go"
            onSubmitEditing={handleUpdatePassword}
          />
          {resetError ? (
            <View className="mb-4 rounded-xl bg-red-900/50 px-4 py-3">
              <Text className="text-sm text-red-300">{resetError}</Text>
            </View>
          ) : null}
          <Pressable
            onPress={handleUpdatePassword}
            disabled={isUpdatingPassword}
            className="items-center rounded-2xl bg-forest-500 px-5 py-4"
          >
            {isUpdatingPassword ? (
              <ActivityIndicator color="#111827" />
            ) : (
              <Text className="text-base font-semibold text-gray-950">Save new password</Text>
            )}
          </Pressable>
        </View>
      </View>
    );
  }

  if (errorMessage) {
    return (
      <View className="flex-1 items-center justify-center bg-gray-950 px-6">
        <View className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-6">
          <Text className="mb-3 text-2xl font-semibold text-white">Authentication Error</Text>
          <Text className="mb-6 text-base leading-6 text-gray-300">{errorMessage}</Text>
          <Pressable
            onPress={() => router.replace('/(auth)/login')}
            className="items-center rounded-2xl bg-forest-500 px-5 py-4"
          >
            <Text className="text-base font-semibold text-gray-950">Back to sign in</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  return (
    <View className="flex-1 items-center justify-center bg-gray-950">
      <ActivityIndicator size="large" color="#3B9062" />
    </View>
  );
}
